<template>
    <div
        class="widget-ai-wizard-overlay"
        @keydown="handleKeyDown"
        @keyup="handleKeyUp"
        tabindex="0"
        ref="wizardOverlay"
    >
        <div class="widget-ai-wizard">
            <div class="wizard-header">
                <h3 class="d-flex align-items-center gap-5">
                    <img src="/static/img/copilot.png" width="30" height="30" />
                    {{ $t('table.supsisAssistantDashboard') }}
                </h3>
                <a-icon type="close" class="close-button" @click="handleClose" />
            </div>

            <!-- Aşama 1: Prompt Girişi -->
            <div v-if="!commands.length" class="prompt-stage">
                <div class="prompt-container">
                    <h4>{{ $t('table.tellAssistantWhatDashboard') }}</h4>
                    <s2-textarea
                        v-model="customerPrompt"
                        :rows="4"
                        :placeholder="$t('table.aiWizardPromptPlaceholder')"
                        class="search-input w-100"
                        ref="promptTextarea"
                    />
                    <a-button
                        type="primary"
                        :loading="copilotWaiting"
                        @click="handleAIRecommendation"
                        class="submit-button"
                    >
                        {{ $t('table.sendToAssistant') }}
                    </a-button>
                </div>

                <!-- Örnek Promptlar Slider -->
                <div class="example-prompts">
                    <h4>{{ $t('table.exampleRequests') }}</h4>
                    <div class="prompt-slider-container">
                        <div class="prompt-slider" ref="slider">
                            <div class="prompt-slide" v-for="(prompt, index) in examplePrompts" :key="index">
                                <div class="prompt-card" @click="selectExamplePrompt(prompt)">
                                    <a-icon type="bulb" />
                                    <p>{{ prompt }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="slider-controls">
                            <a-button class="control-btn prev" @click="slidePrev">
                                <a-icon type="left" />
                            </a-button>
                            <a-button class="control-btn next" @click="slideNext">
                                <a-icon type="right" />
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Aşama 2: İşlem Adımları -->
            <div v-else>
                <a-progress :percent="progressPercent" stroke-color="#1890ff" show-info style="margin-bottom: 16px" />
                <!-- Adım Listesi -->
                <div class="wizard-steps">
                    <div v-for="(step, index) in steps" :key="index" class="wizard-step" :class="step.status">
                        <a-icon
                            v-if="step.status === 'waiting'"
                            type="pause-circle"
                            theme="filled"
                            style="color: #ccc; margin-right: 8px"
                        />
                        <a-icon
                            v-else-if="step.status === 'in-progress'"
                            type="loading"
                            style="color: #1890ff; margin-right: 8px"
                        />
                        <a-icon
                            v-else-if="step.status === 'success'"
                            type="check-circle"
                            theme="filled"
                            style="color: #52c41a; margin-right: 8px"
                        />
                        <a-icon
                            v-else-if="step.status === 'error'"
                            type="close-circle"
                            theme="filled"
                            style="color: #f5222d; margin-right: 8px"
                        />
                        <span>{{ step.displayLabel }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CoPilot from "../../../../../utils/CoPilot";
import confetti from "canvas-confetti";
import { TABLE_API } from "../../../../../api/table";
import S2Textarea from "../../../../../components/S2Textarea.vue";

export default {
    name: "WidgetAiWizard",
    components: {
        S2Textarea
    },
    props: {
        table: {
            type: Object,
            required: true,
        },
        page: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            steps: [],
            currentCommandIndex: 0,
            totalCommands: 0,
            commands: [],
            copilotWaiting: false,
            customerPrompt: "",
            examplePrompts: [
                "Satış performansını gösteren grafikler ve sayısal veriler istiyorum. Toplam satış, ortalama satış ve en yüksek satışı göster.",
                "Müşteri memnuniyetini takip etmek için dashboard oluştur. Memnuniyet puanlarını grafik ve sayısal olarak göster.",
                "Stok durumunu gösteren bir dashboard istiyorum. Kritik stok seviyesindeki ürünleri ve stok trendini göster.",
                "Personel performansını izlemek için dashboard oluştur. Kişi bazlı satış grafikleri ve hedef gerçekleştirme oranlarını göster.",
                "Proje ilerlemesini gösteren dashboard istiyorum. Tamamlanan görevler, geciken işler ve zaman çizelgesini göster.",
                "Finansal göstergeleri takip etmek için dashboard oluştur. Gelir, gider ve kar grafiklerini göster.",
                "Üretim verimliliğini izlemek için dashboard istiyorum. Üretim miktarı, fire oranı ve kapasite kullanımını göster.",
                "Sosyal medya performansını gösteren dashboard oluştur. Etkileşim oranları ve takipçi artışını göster.",
            ],
            currentSlide: 0,
            slideInterval: null,
        };
    },
    computed: {
        progressPercent() {
            if (this.totalCommands === 0) {
                return 0;
            }
            const completedCount = this.steps.filter((s) => s.status === "success").length;
            return Math.round((completedCount / this.totalCommands) * 100);
        },
    },
    mounted() {
        this.startSlideShow();
        // Form overlay'ine focus ver - böylece keydown eventlerini alabilir
        this.$nextTick(() => {
            if (this.$refs.wizardOverlay) {
                this.$refs.wizardOverlay.focus();
            }
        });
    },
    beforeDestroy() {
        this.stopSlideShow();
    },
    methods: {
        async handleAIRecommendation() {
            try {
                this.copilotWaiting = true;
                const copilot = CoPilot.getInstance();
                const recommendation = await copilot.createTableReports({
                    siteId: this.$store.state.site.site._id,
                    tableId: this.table._id,
                    pageId: this.page._id,
                    customerPrompt: this.customerPrompt,
                });

                if (!recommendation) {
                    this.$message.error("AI önerileri alınamadı");
                    this.copilotWaiting = false;
                    return;
                }

                this.commands = recommendation;
                this.initSteps();
                this.startProcessing();
            } catch (error) {
                console.error(error);
                this.$message.error("AI önerileri alınamadı");
            } finally {
                this.copilotWaiting = false;
            }
        },

        initSteps() {
            const localCommands = this.commands || [];
            this.totalCommands = localCommands.length;

            this.steps = localCommands.map((cmd, index) => {
                const displayLabel = this.createDisplayLabel(cmd);
                return {
                    displayLabel: displayLabel,
                    status: "waiting",
                    commandIndex: index,
                };
            });
        },

        createDisplayLabel(cmd) {
            if (cmd.operation === "create-widget") {
                return `${cmd.payload.type} tipinde ${cmd.payload.title} isimli widget oluşturulması`;
            }
            return "Bilinmeyen İşlem";
        },

        async startProcessing() {
            this.processNextCommand();
        },

        sleep(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },

        async processNextCommand() {
            await this.sleep(Math.random() * 500 + 500);

            if (this.currentCommandIndex >= this.totalCommands) {
                this.$message.success("Dashboard için gerekli olan widgetlar oluşturuldu");
                setTimeout(() => {
                    this.runSuccessConfetti();
                    this.$emit("close");
                }, 1000);
                return;
            }

            const step = this.steps[this.currentCommandIndex];
            step.status = "in-progress";

            const cmd = this.commands[this.currentCommandIndex];
            this.executeCommand(cmd)
                .then(() => {
                    step.status = "success";
                    this.currentCommandIndex++;
                    this.processNextCommand();
                })
                .catch((err) => {
                    step.status = "error";
                    console.error("Komut yürütülürken hata oluştu:", err);
                });
        },

        async executeCommand(cmd) {
            if (!cmd || !cmd.operation) {
                return Promise.reject(new Error("Geçersiz komut"));
            }

            if (cmd.operation === "create-widget") {
                return this.handleCreateWidget(cmd.payload);
            }

            return Promise.reject(new Error("Desteklenmeyen operasyon: " + cmd.operation));
        },

        async handleCreateWidget(payload) {
            console.log("handleCreateWidget", payload);
            const widget = await TABLE_API.createWidget(
                this.table._id,
                this.page._id,
                payload.type,
                payload.settings,
                payload.title
            );
            this.$emit("widget-added", widget);
            return widget;
        },

        handleClose() {
            this.$emit("close");
        },

        startSlideShow() {
            this.slideInterval = setInterval(() => {
                this.slideNext();
            }, 5000);
        },

        stopSlideShow() {
            if (this.slideInterval) {
                clearInterval(this.slideInterval);
            }
        },

        slideNext() {
            const slider = this.$refs.slider;
            this.currentSlide = (this.currentSlide + 1) % (this.examplePrompts.length - 1);
            slider.scrollTo({
                left: this.currentSlide * (slider.offsetWidth / 2),
                behavior: "smooth",
            });
        },

        slidePrev() {
            const slider = this.$refs.slider;
            this.currentSlide = this.currentSlide === 0 ? this.examplePrompts.length - 2 : this.currentSlide - 1;
            slider.scrollTo({
                left: this.currentSlide * (slider.offsetWidth / 2),
                behavior: "smooth",
            });
        },

        selectExamplePrompt(prompt) {
            this.customerPrompt = prompt;
            this.$nextTick(() => {
                const textArea = document.querySelector(".prompt-container textarea");
                if (textArea) {
                    textArea.focus();
                    textArea.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            });
        },

        runSuccessConfetti() {
            const defaults = {
                spread: 360,
                ticks: 50,
                gravity: 0,
                decay: 0.94,
                startVelocity: 30,
                colors: ["FFE400", "FFBD00", "E89400", "FFCA6C", "FDFFB8"],
            };

            const shoot = () => {
                confetti({
                    ...defaults,
                    particleCount: 40,
                    scalar: 1.2,
                    shapes: ["star"],
                });

                confetti({
                    ...defaults,
                    particleCount: 10,
                    scalar: 0.75,
                    shapes: ["circle"],
                });
            };

            shoot();
            setTimeout(shoot, 200);
            setTimeout(shoot, 300);
            setTimeout(shoot, 400);
        },

        handleKeyDown(event) {
            // Ana komponent üzerinde keydown eventi gerekirse kullanılabilir
        },
        
        handleKeyUp(event) {
            // Ana komponent üzerinde keyup eventi gerekirse kullanılabilir
        }
    },
};
</script>

<style lang="scss" scoped>
.widget-ai-wizard-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    .widget-ai-wizard {
        background: #fff;
        width: 80%;
        height: 80%;
        border-radius: 8px;
        padding: 24px;
        position: relative;
        overflow: auto;
    }
}

.prompt-stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    gap: 40px;

    .prompt-container {
        width: 100%;
        max-width: 600px;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;

        h4 {
            margin-bottom: 16px;
        }

        .submit-button {
            margin-top: 16px;
        }
    }
}

.example-prompts {
    width: 100%;
    max-width: 800px;

    h4 {
        text-align: center;
        margin-bottom: 16px;
    }

    .prompt-slider-container {
        position: relative;
        width: 100%;
        max-width: 800px;
        margin: 0 auto;
        padding: 0 40px;

        .prompt-slider {
            display: flex;
            overflow-x: hidden;
            scroll-snap-type: x mandatory;
            gap: 24px;
            padding: 16px 0;
            scroll-behavior: smooth;

            .prompt-slide {
                flex: 0 0 calc(50% - 12px);
                scroll-snap-align: start;
            }
        }

        .slider-controls {
            position: absolute;
            width: calc(100% + 80px);
            top: 50%;
            left: -40px;
            transform: translateY(-50%);
            display: flex;
            justify-content: space-between;
            pointer-events: none;

            .control-btn {
                pointer-events: auto;
                background: white;
                border: 1px solid #e8e8e8;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.3s ease;
                border-radius: 50%;
                z-index: 2;

                .anticon {
                    font-size: 18px;
                }

                &:hover {
                    background: #1890ff;
                    border-color: #1890ff;
                    color: white;
                }
            }
        }
    }
}

.prompt-card {
    background: #f0f7ff;
    border: 1px solid #d9e8ff;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    cursor: pointer;

    .anticon {
        font-size: 32px;
        color: #1890ff;
        margin-bottom: 16px;
    }

    p {
        margin: 0;
        color: #454545;
        font-size: 14px;
        line-height: 1.6;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
        background: #e6f1ff;
        cursor: pointer;
    }

    &:active {
        transform: scale(0.98);
    }
}

.wizard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    h3 {
        margin: 0;
    }

    .close-button {
        cursor: pointer;
        font-size: 20px;
        transition: color 0.3s;

        &:hover {
            color: #ff4d4f;
        }
    }
}

.wizard-steps {
    padding: 24px;
    background: #fafafa;
    border-radius: 8px;

    .wizard-step {
        display: flex;
        align-items: center;
        padding: 12px;
        margin-bottom: 8px;
        background: white;
        border-radius: 4px;
        transition: all 0.3s;

        &.waiting {
            opacity: 0.6;
        }

        &.in-progress {
            background: #e6f7ff;
            border: 1px solid #91d5ff;
        }

        &.success {
            background: #f6ffed;
            border: 1px solid #b7eb8f;
        }

        &.error {
            background: #fff1f0;
            border: 1px solid #ffa39e;
        }

        span {
            margin-left: 8px;
        }
    }
}
</style>
